import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Grid, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Card, CardContent, LinearProgress } from '@mui/material';
import { advancedAnalyticsAPI } from '../services/api';

const AdvancedAnalytics = () => {
  const [mainTab, setMainTab] = useState(0);
  const [subTab, setSubTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states for all analytics
  const [hourlyData, setHourlyData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [adminData, setAdminData] = useState([]);
  const [complaintTypeData, setComplaintTypeData] = useState([]);
  const [dropReasons, setDropReasons] = useState([]);
  const [feedbackSentiment, setFeedbackSentiment] = useState([]);
  const [districtRankings, setDistrictRankings] = useState([]);
  const [categoryTrends, setCategoryTrends] = useState([]);
  const [categoryComparison, setCategoryComparison] = useState([]);
  const [repeatComplainants, setRepeatComplainants] = useState([]);
  const [resolutionTime, setResolutionTime] = useState([]);
  const [statusFlow, setStatusFlow] = useState([]);
  const [monthlyYearly, setMonthlyYearly] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [userSegmentation, setUserSegmentation] = useState([]);
  const [performanceIndicators, setPerformanceIndicators] = useState([]);
  const [complianceReport, setComplianceReport] = useState([]);
  const [executiveSummary, setExecutiveSummary] = useState(null);

  // NEW: Department and Geographic Analytics
  const [districtDetailedData, setDistrictDetailedData] = useState([]);
  const [tehsilData, setTehsilData] = useState([]);
  const [complaintTypeAnalytics, setComplaintTypeAnalytics] = useState([]);
  const [geographicHotspots, setGeographicHotspots] = useState([]);
  const [deptDistrictCross, setDeptDistrictCross] = useState([]);
  const [severityClassification, setSeverityClassification] = useState([]);
  const [departmentPerformance, setDepartmentPerformance] = useState([]);
  const [complaintVolumeTrends, setComplaintVolumeTrends] = useState([]);

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    try {
      setLoading(true);
      const [
        hourly, device, admin, complaintType, dropReason, feedback,
        district, catTrend, catComp, repeat, resolution, status,
        monthYear, escalation, userSeg, performance, compliance, executive,
        districtDetailed, tehsils, complaintTypeAna, geoHotspots,
        deptDistrictAna, severity, deptPerf, complaintVolume
      ] = await Promise.all([
        advancedAnalyticsAPI.getHourlyAnalytics(),
        advancedAnalyticsAPI.getDeviceAnalytics(),
        advancedAnalyticsAPI.getAdminPerformance(),
        advancedAnalyticsAPI.getComplaintTypeDetailed(),
        advancedAnalyticsAPI.getDropReasons(),
        advancedAnalyticsAPI.getFeedbackSentiment(),
        advancedAnalyticsAPI.getDistrictRankings(),
        advancedAnalyticsAPI.getCategoryTrends(),
        advancedAnalyticsAPI.getCategoryComparison(),
        advancedAnalyticsAPI.getRepeatComplainants(50),
        advancedAnalyticsAPI.getResolutionTimeAnalytics(),
        advancedAnalyticsAPI.getStatusFlowAnalytics(),
        advancedAnalyticsAPI.getMonthlyYearlyTrends(),
        advancedAnalyticsAPI.getEscalationAnalytics(),
        advancedAnalyticsAPI.getUserSegmentation(),
        advancedAnalyticsAPI.getPerformanceIndicators(),
        advancedAnalyticsAPI.getComplianceReport(),
        advancedAnalyticsAPI.getExecutiveSummary(),
        advancedAnalyticsAPI.getDistrictDetailedAnalytics(),
        advancedAnalyticsAPI.getTehsilAnalytics(),
        advancedAnalyticsAPI.getComplaintTypeAnalytics(),
        advancedAnalyticsAPI.getGeographicHotspots(),
        advancedAnalyticsAPI.getDepartmentDistrictCrossAnalytics(),
        advancedAnalyticsAPI.getSeverityClassification(),
        advancedAnalyticsAPI.getDepartmentPerformance(),
        advancedAnalyticsAPI.getComplaintVolumeTrends()
      ]);

      if (hourly.data.success) setHourlyData(hourly.data.data.rows || []);
      if (device.data.success) setDeviceData(device.data.data.rows || []);
      if (admin.data.success) setAdminData(admin.data.data.rows || []);
      if (complaintType.data.success) setComplaintTypeData(complaintType.data.data.rows || []);
      if (dropReason.data.success) setDropReasons(dropReason.data.data.rows || []);
      if (feedback.data.success) setFeedbackSentiment(feedback.data.data.rows || []);
      if (district.data.success) setDistrictRankings(district.data.data.rows || []);
      if (catTrend.data.success) setCategoryTrends(catTrend.data.data.rows || []);
      if (catComp.data.success) setCategoryComparison(catComp.data.data.rows || []);
      if (repeat.data.success) setRepeatComplainants(repeat.data.data.rows || []);
      if (resolution.data.success) setResolutionTime(resolution.data.data.rows || []);
      if (status.data.success) setStatusFlow(status.data.data.rows || []);
      if (monthYear.data.success) setMonthlyYearly(monthYear.data.data.rows || []);
      if (escalation.data.success) setEscalations(escalation.data.data.rows || []);
      if (userSeg.data.success) setUserSegmentation(userSeg.data.data.rows || []);
      if (performance.data.success) setPerformanceIndicators(performance.data.data.rows || []);
      if (compliance.data.success) setComplianceReport(compliance.data.data.rows || []);
      if (executive.data.success) setExecutiveSummary(executive.data.data);

      // NEW: Department and Geographic Analytics
      if (districtDetailed.data.success) setDistrictDetailedData(districtDetailed.data.data.rows || []);
      if (tehsils.data.success) setTehsilData(tehsils.data.data.rows || []);
      if (complaintTypeAna.data.success) setComplaintTypeAnalytics(complaintTypeAna.data.data.rows || []);
      if (geoHotspots.data.success) setGeographicHotspots(geoHotspots.data.data.rows || []);
      if (deptDistrictAna.data.success) setDeptDistrictCross(deptDistrictAna.data.data.rows || []);
      if (severity.data.success) setSeverityClassification(severity.data.data.rows || []);
      if (deptPerf.data.success) setDepartmentPerformance(deptPerf.data.data.rows || []);
      if (complaintVolume.data.success) setComplaintVolumeTrends(complaintVolume.data.data.rows || []);
    } catch (err) {
      setError('Error loading advanced analytics: ' + err.message);
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMainTabChange = (event, newValue) => {
    setMainTab(newValue);
    setSubTab(0);
  };

  const handleSubTabChange = (event, newValue) => {
    setSubTab(newValue);
  };

  if (error) {
    return <Box sx={{ p: 3 }}><Typography color="error">{error}</Typography></Box>;
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 3, px: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
          Advanced Analytics & Insights
        </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280', fontSize: '0.95rem' }}>
          Comprehensive analysis with 18+ advanced metrics and insights
        </Typography>
      </Box>

      {/* Executive Summary Section */}
      {executiveSummary && (
        <Paper elevation={0} sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 2, p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Executive Summary</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.85rem', mb: 1 }}>Total Complaints</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea' }}>{executiveSummary.total_complaints || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.85rem', mb: 1 }}>Overall Satisfaction</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>{executiveSummary.overall_satisfaction || 0}/5</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.85rem', mb: 1 }}>Unique Users</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>{executiveSummary.unique_users || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.85rem', mb: 1 }}>Active Admins</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444' }}>{executiveSummary.active_admins || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Main Tabs */}
      <Paper elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 2 }}>
        <Tabs value={mainTab} onChange={handleMainTabChange} sx={{ borderBottom: '1px solid #e5e7eb', overflowX: 'auto' }}>
          <Tab label="Time & Patterns" />
          <Tab label="Performance" />
          <Tab label="Geographic" />
          <Tab label="Categories" />
          <Tab label="Users" />
          <Tab label="Quality Metrics" />
          <Tab label="Departments" />
          <Tab label="Geo Enhanced" />
          <Tab label="Severity" />
        </Tabs>

        <Box sx={{ p: 2, px: 3 }}>
          {/* TIME & PATTERNS TAB */}
          {mainTab === 0 && (
            <Box>
              <Tabs value={subTab} onChange={handleSubTabChange} sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}>
                <Tab label="Hourly Distribution" />
                <Tab label="Monthly/Yearly Trends" />
                <Tab label="Device Analytics" />
              </Tabs>

              {subTab === 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Hourly Complaint Distribution (Last 30 Days)</Typography>
                  {hourlyData.length > 0 ? (
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Hour</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Created</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>In Progress</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolution %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {hourlyData.map((row) => (
                          <TableRow key={row.hour_of_day} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                            <TableCell sx={{ fontWeight: 500 }}>{String(row.hour_of_day).padStart(2, '0')}:00</TableCell>
                            <TableCell align="right">{row.complaints_created}</TableCell>
                            <TableCell align="right">{row.resolved}</TableCell>
                            <TableCell align="right">{row.in_progress}</TableCell>
                            <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>{row.resolution_rate}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No data</Typography>
                  )}
                </Box>
              )}

              {subTab === 1 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>36-Month Historical Trends</Typography>
                  {monthlyYearly.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Dropped</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Rate %</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Feedback</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monthlyYearly.slice(0, 24).map((row) => (
                          <TableRow key={row.month} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.9rem' }}>
                            <TableCell sx={{ fontWeight: 500 }}>{row.month}</TableCell>
                            <TableCell align="right">{row.total_complaints}</TableCell>
                            <TableCell align="right">{row.resolved}</TableCell>
                            <TableCell align="right">{row.dropped}</TableCell>
                            <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>{row.resolution_rate}%</TableCell>
                            <TableCell align="right">{row.feedback_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No data</Typography>
                  )}
                </Box>
              )}

              {subTab === 2 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Complaints by Platform/Device</Typography>
                  {deviceData.length > 0 ? (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {deviceData.map((row) => (
                        <Grid item xs={12} sm={6} md={4} key={row.device}>
                          <Card sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                            <CardContent>
                              <Typography sx={{ color: '#6b7280', fontSize: '0.9rem', mb: 1 }}>
                                {row.device || 'Unknown Device'}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
                                {row.total_complaints}
                              </Typography>
                              <LinearProgress variant="determinate" value={row.percentage_of_total} sx={{ mb: 1 }} />
                              <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                                {row.percentage_of_total}% of total
                              </Typography>
                              <Typography sx={{ color: '#10b981', fontSize: '0.85rem', mt: 1 }}>
                                Resolution: {row.resolution_rate}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No device data</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* PERFORMANCE TAB */}
          {mainTab === 1 && (
            <Box>
              <Tabs value={subTab} onChange={handleSubTabChange} sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}>
                <Tab label="Admin Performance" />
                <Tab label="Status Flow" />
                <Tab label="Drop Reasons" />
                <Tab label="Performance Indicators" />
              </Tabs>

              {subTab === 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Top 50 Admin Performance (Last 90 Days)</Typography>
                  {adminData.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Admin Name</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Handled</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Dropped</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Days</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>This Week</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {adminData.map((row) => (
                          <TableRow key={row.admin_id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.85rem' }}>
                            <TableCell sx={{ fontWeight: 500 }}>{row.admin_name}</TableCell>
                            <TableCell align="right">{row.complaints_handled}</TableCell>
                            <TableCell align="right" sx={{ color: '#10b981' }}>{row.resolved_by_admin}</TableCell>
                            <TableCell align="right" sx={{ color: '#ef4444' }}>{row.dropped}</TableCell>
                            <TableCell align="right">{row.avg_days_to_forward}</TableCell>
                            <TableCell align="right">{row.tracks_this_week}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No admin data</Typography>
                  )}
                </Box>
              )}

              {subTab === 1 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Complaint Status Flow</Typography>
                  {statusFlow.length > 0 ? (
                    <Grid container spacing={2}>
                      {statusFlow.map((row) => {
                        let bgColor = '#f9fafb';
                        let statusColor = '#667eea';
                        if (row.current_status === 'rs') { bgColor = '#ecfdf5'; statusColor = '#10b981'; }
                        else if (row.current_status === 'dp') { bgColor = '#fef2f2'; statusColor = '#ef4444'; }
                        else if (row.current_status === 'ip') { bgColor = '#fffbeb'; statusColor = '#f59e0b'; }

                        return (
                          <Grid item xs={12} sm={6} md={4} key={row.current_status}>
                            <Card sx={{ bgcolor: bgColor, border: `2px solid ${statusColor}` }}>
                              <CardContent sx={{ textAlign: 'center' }}>
                                <Typography sx={{ color: '#6b7280', fontSize: '0.9rem', mb: 1, fontWeight: 600 }}>
                                  {row.current_status === 'rs' ? 'Resolved' : row.current_status === 'dp' ? 'Dropped' : 'In Progress'}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: statusColor, mb: 1 }}>
                                  {row.complaint_count}
                                </Typography>
                                <LinearProgress variant="determinate" value={row.percentage_of_total} />
                                <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem', mt: 1 }}>
                                  {row.percentage_of_total}% of total
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No status data</Typography>
                  )}
                </Box>
              )}

              {subTab === 2 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Drop/Rejection Reasons Analysis</Typography>
                  {dropReasons.length > 0 ? (
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Drop Reason</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>% of Dropped</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Days to Drop</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Admins</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dropReasons.map((row) => (
                          <TableRow key={row.drop_reason} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                            <TableCell>{row.drop_reason}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{row.complaint_count}</TableCell>
                            <TableCell align="right" sx={{ color: '#ef4444', fontWeight: 600 }}>{row.percentage_of_dropped}%</TableCell>
                            <TableCell align="right">{row.avg_days_to_drop}</TableCell>
                            <TableCell align="right">{row.admins_involved}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No drop reason data</Typography>
                  )}
                </Box>
              )}

              {subTab === 3 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Key Performance Indicators</Typography>
                  {performanceIndicators.length > 0 ? (
                    <Grid container spacing={2}>
                      {performanceIndicators.slice(0, 4).map((row) => (
                        <Grid item xs={12} sm={6} key={row.indicator}>
                          <Card sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                            <CardContent>
                              <Typography sx={{ color: '#6b7280', fontSize: '0.9rem', mb: 1, fontWeight: 600 }}>
                                {row.indicator}
                              </Typography>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
                                {row.current_value}{row.unit === 'percentage' ? '%' : row.unit === 'rating' ? '' : ' days'}
                              </Typography>
                              <Box sx={{ mb: 1 }}>
                                <Typography sx={{ fontSize: '0.85rem', color: '#9ca3af' }}>Target: {row.target_value}</Typography>
                                <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Last 30d: {row.last_30_days}</Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={Math.min((row.current_value / row.target_value) * 100, 100)} />
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No KPI data</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* GEOGRAPHIC TAB */}
          {mainTab === 2 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>District-wise Rankings & Performance</Typography>
              {districtRankings.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                      <TableCell sx={{ fontWeight: 600 }}>District</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Rate %</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Pending</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Satisfaction</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Age</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {districtRankings.map((row) => (
                      <TableRow key={row.district_id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.85rem' }}>
                        <TableCell sx={{ fontWeight: 500 }}>{row.district_name}</TableCell>
                        <TableCell align="right">{row.total_complaints}</TableCell>
                        <TableCell align="right">{row.resolved}</TableCell>
                        <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>{row.resolution_rate}%</TableCell>
                        <TableCell align="right" sx={{ color: '#f59e0b' }}>{row.in_progress}</TableCell>
                        <TableCell align="right">{row.avg_satisfaction || 'N/A'}</TableCell>
                        <TableCell align="right">{row.avg_age_days} days</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No district data</Typography>
              )}
            </Box>
          )}

          {/* CATEGORIES TAB */}
          {mainTab === 3 && (
            <Box>
              <Tabs value={subTab} onChange={handleSubTabChange} sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}>
                <Tab label="Category Comparison" />
                <Tab label="Category Trends" />
                <Tab label="Complaint Types" />
                <Tab label="Feedback Sentiment" />
              </Tabs>

              {subTab === 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Top 30 Categories Comparative Analysis</Typography>
                  {categoryComparison.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Rate %</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Rating</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Overdue</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Age</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryComparison.slice(0, 20).map((row) => (
                          <TableRow key={row.category_id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.8rem' }}>
                            <TableCell sx={{ fontWeight: 500 }}>{row.category_name}</TableCell>
                            <TableCell align="right">{row.total_complaints}</TableCell>
                            <TableCell align="right">{row.resolved}</TableCell>
                            <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>{row.resolution_rate}%</TableCell>
                            <TableCell align="right">{row.avg_rating || 'N/A'}</TableCell>
                            <TableCell align="right" sx={{ color: row.overdue_complaints > 20 ? '#ef4444' : '#667eea' }}>{row.overdue_complaints}</TableCell>
                            <TableCell align="right">{row.avg_age_days} days</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No comparison data</Typography>
                  )}
                </Box>
              )}

              {subTab === 1 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Category Weekly Trends (12 Weeks)</Typography>
                  {categoryTrends.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Week</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Prev Week</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>WoW Change %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryTrends.slice(0, 30).map((row, idx) => {
                          const change = row.week_over_week_change;
                          const changeColor = change > 0 ? '#ef4444' : change < 0 ? '#10b981' : '#667eea';
                          return (
                            <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.8rem' }}>
                              <TableCell sx={{ fontWeight: 500 }}>{row.category_name}</TableCell>
                              <TableCell align="right">{row.week_start}</TableCell>
                              <TableCell align="right">{row.week_count}</TableCell>
                              <TableCell align="right">{row.prev_week_count || '-'}</TableCell>
                              <TableCell align="right" sx={{ color: changeColor, fontWeight: 600 }}>
                                {change ? `${change > 0 ? '+' : ''}${change}%` : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No trend data</Typography>
                  )}
                </Box>
              )}

              {subTab === 2 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Complaint Type Distribution</Typography>
                  {complaintTypeData.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>% of Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Satisfaction</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Satisfied Users</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {complaintTypeData.map((row) => (
                          <TableRow key={row.complaint_type} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.85rem' }}>
                            <TableCell sx={{ fontWeight: 500 }}>{row.complaint_type}</TableCell>
                            <TableCell align="right">{row.total_count}</TableCell>
                            <TableCell align="right" sx={{ color: '#667eea' }}>{row.percentage_of_total}%</TableCell>
                            <TableCell align="right">{row.resolved}</TableCell>
                            <TableCell align="right">{row.avg_satisfaction || 'N/A'}</TableCell>
                            <TableCell align="right" sx={{ color: '#10b981' }}>{row.satisfied_users}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No type data</Typography>
                  )}
                </Box>
              )}

              {subTab === 3 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Feedback Sentiment Analysis</Typography>
                  {feedbackSentiment.length > 0 ? (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {feedbackSentiment.map((row) => {
                        let bgColor = '#f9fafb';
                        let borderColor = '#667eea';
                        if (row.sentiment === 'Positive') { bgColor = '#ecfdf5'; borderColor = '#10b981'; }
                        else if (row.sentiment === 'Negative') { bgColor = '#fef2f2'; borderColor = '#ef4444'; }
                        else { bgColor = '#fffbeb'; borderColor = '#f59e0b'; }

                        return (
                          <Grid item xs={12} sm={6} md={4} key={row.sentiment}>
                            <Card sx={{ bgcolor: bgColor, border: `2px solid ${borderColor}` }}>
                              <CardContent sx={{ textAlign: 'center' }}>
                                <Typography sx={{ fontWeight: 600, mb: 1, color: borderColor }}>{row.sentiment}</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: borderColor, mb: 1 }}>{row.feedback_count}</Typography>
                                <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem', mb: 1 }}>{row.percentage}% of feedback</Typography>
                                <Typography sx={{ color: '#6b7280', fontSize: '0.85rem' }}>Avg Rating: {row.avg_rating}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No sentiment data</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* USERS TAB */}
          {mainTab === 4 && (
            <Box>
              <Tabs value={subTab} onChange={handleSubTabChange} sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}>
                <Tab label="Repeat Complainants" />
                <Tab label="User Segmentation" />
                <Tab label="Escalation Analysis" />
              </Tabs>

              {subTab === 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Top 50 Repeat Complainants</Typography>
                  {repeatComplainants.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>User Name</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Complaints</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Rating</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Categories</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Days Span</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {repeatComplainants.slice(0, 30).map((row) => (
                          <TableRow key={row.user_id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.8rem' }}>
                            <TableCell sx={{ fontWeight: 500 }}>{row.user_name}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#667eea' }}>{row.total_complaints}</TableCell>
                            <TableCell align="right">{row.resolved}</TableCell>
                            <TableCell align="right" sx={{ color: row.avg_satisfaction >= 3 ? '#10b981' : '#ef4444' }}>{row.avg_satisfaction || 'N/A'}</TableCell>
                            <TableCell align="right">{row.unique_categories}</TableCell>
                            <TableCell align="right">{row.complaint_span_days}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No repeat complainant data</Typography>
                  )}
                </Box>
              )}

              {subTab === 1 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>User Segmentation Analysis</Typography>
                  {userSegmentation.length > 0 ? (
                    <Grid container spacing={2}>
                      {userSegmentation.map((row) => (
                        <Grid item xs={12} sm={6} md={3} key={row.user_segment}>
                          <Card sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                            <CardContent>
                              <Typography sx={{ color: '#6b7280', fontSize: '0.9rem', mb: 1, fontWeight: 600 }}>
                                {row.user_segment}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
                                {row.user_count} users
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                                  Complaints: {row.total_complaints}
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                                  Avg per user: {row.avg_complaints_per_user}
                                </Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 600 }}>
                                Satisfied: {row.satisfaction_percentage}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No segmentation data</Typography>
                  )}
                </Box>
              )}

              {subTab === 2 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Multi-Track Escalation Analysis (200 records)</Typography>
                  {escalations.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Tracking ID</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Tracks</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Admins</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Days Open</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Forwarded</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Rating</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {escalations.slice(0, 25).map((row) => (
                          <TableRow key={row.complaint_id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.75rem' }}>
                            <TableCell sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.tracking_id}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: row.total_tracks > 2 ? '#ef4444' : '#667eea' }}>{row.total_tracks}</TableCell>
                            <TableCell align="right">{row.admins_involved}</TableCell>
                            <TableCell align="right">{row.days_in_system}</TableCell>
                            <TableCell align="right">{row.forwarded_count}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{row.final_status}</TableCell>
                            <TableCell align="right">{row.user_rating || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No escalation data</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* QUALITY METRICS TAB */}
          {mainTab === 5 && (
            <Box>
              <Tabs value={subTab} onChange={handleSubTabChange} sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}>
                <Tab label="Resolution Time" />
                <Tab label="Compliance Report" />
              </Tabs>

              {subTab === 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Resolution Time Statistics</Typography>
                  {resolutionTime.length > 0 && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {resolutionTime.map((row) => (
                        <Grid item xs={12} sm={6} md={3} key={row.metric_type}>
                          <Card sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                            <CardContent sx={{ textAlign: 'center', p: 1.5 }}>
                              <Typography sx={{ color: '#6b7280', fontSize: '0.8rem', mb: 0.5 }}>Average</Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea' }}>{row.avg_days_open} days</Typography>
                              <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem', mt: 1 }}>
                                Min: {row.min_days} | Max: {row.max_days}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}

              {subTab === 1 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Compliance Metrics Report</Typography>
                  {complianceReport.length > 0 ? (
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Compliance %</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Completed</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Interpretation</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {complianceReport.map((row) => {
                          const color = row.compliance_percentage >= 80 ? '#10b981' : row.compliance_percentage >= 50 ? '#f59e0b' : '#ef4444';
                          return (
                            <TableRow key={row.compliance_metric} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                              <TableCell sx={{ fontWeight: 600 }}>{row.compliance_metric}</TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                  <Typography sx={{ color, fontWeight: 600 }}>{row.compliance_percentage}%</Typography>
                                  <Box sx={{ width: 100, height: 8, bgcolor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: `${row.compliance_percentage}%`, bgcolor: color }} />
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="right">{row.completed_count}</TableCell>
                              <TableCell align="right">{row.total_count}</TableCell>
                              <TableCell sx={{ fontSize: '0.85rem', color: '#6b7280' }}>{row.interpretation}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>No compliance data</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* DEPARTMENTS TAB */}
          {mainTab === 6 && (
            <Box>
              <Tabs value={subTab} onChange={handleSubTabChange} sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}>
                <Tab label="Department Performance" />
                <Tab label="Department-District" />
              </Tabs>

              {subTab === 0 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Department Performance Matrix</Typography>
                  <Table size="small" sx={{ fontSize: '0.9rem' }}>
                    <TableHead sx={{ bgcolor: '#f3f4f6' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Total Complaints</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Resolution Rate %</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Feedback</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Days</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>This Week</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>This Month</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Districts</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {departmentPerformance.slice(0, 30).map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{row.department_name}</TableCell>
                          <TableCell align="right">{row.total_complaints || 0}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ bgcolor: '#dbeafe', p: '4px 8px', borderRadius: 1, display: 'inline-block', color: '#0284c7' }}>
                              {row.resolution_rate || 0}%
                            </Box>
                          </TableCell>
                          <TableCell align="right">{row.avg_feedback_rating || 'N/A'}</TableCell>
                          <TableCell align="right">{row.avg_resolution_time_days || 0}</TableCell>
                          <TableCell align="right">{row.complaints_this_week || 0}</TableCell>
                          <TableCell align="right">{row.complaints_this_month || 0}</TableCell>
                          <TableCell align="right">{row.districts_covered || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {subTab === 1 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Departments by District</Typography>
                  <Table size="small" sx={{ fontSize: '0.9rem' }}>
                    <TableHead sx={{ bgcolor: '#f3f4f6' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>District</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Complaints</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Res. Rate %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deptDistrictCross.slice(0, 50).map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.85rem' }}>
                          <TableCell>{row.department_name}</TableCell>
                          <TableCell>{row.district_name || 'N/A'}</TableCell>
                          <TableCell align="right">{row.complaints || 0}</TableCell>
                          <TableCell align="right">{row.resolved || 0}</TableCell>
                          <TableCell align="right" sx={{ color: row.resolution_rate > 70 ? '#10b981' : '#f59e0b' }}>
                            {row.resolution_rate || 0}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}

          {/* GEO ENHANCED TAB */}
          {mainTab === 7 && (
            <Box>
              <Tabs value={subTab} onChange={handleSubTabChange} sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}>
                <Tab label="Districts Detailed" />
                <Tab label="Tehsils" />
                <Tab label="Hotspots" />
              </Tabs>

              {subTab === 0 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Detailed District Analytics</Typography>
                  <Table size="small" sx={{ fontSize: '0.9rem' }}>
                    <TableHead sx={{ bgcolor: '#f3f4f6' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>District</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Complaints</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Users</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Resolution %</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Satisfaction</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Days Open</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Last 30 Days</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {districtDetailedData.slice(0, 37).map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{row.district_name}</TableCell>
                          <TableCell align="right">{row.total_complaints || 0}</TableCell>
                          <TableCell align="right">{row.unique_complainants || 0}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ bgcolor: '#dcfce7', p: '4px 8px', borderRadius: 1, display: 'inline-block', color: '#16a34a' }}>
                              {row.resolution_rate || 0}%
                            </Box>
                          </TableCell>
                          <TableCell align="right">{row.avg_satisfaction || 0}</TableCell>
                          <TableCell align="right">{row.avg_complaint_age || 0}</TableCell>
                          <TableCell align="right">{row.complaints_last_30_days || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {subTab === 1 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Tehsil-Level Analytics</Typography>
                  <Table size="small" sx={{ fontSize: '0.9rem' }}>
                    <TableHead sx={{ bgcolor: '#f3f4f6' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Tehsil</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>District</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Complaints</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Res. Rate %</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Days</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tehsilData.slice(0, 40).map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.85rem' }}>
                          <TableCell>{row.tehsil_name}</TableCell>
                          <TableCell>{row.district_name}</TableCell>
                          <TableCell align="right">{row.complaints_count || 0}</TableCell>
                          <TableCell align="right">{row.resolved || 0}</TableCell>
                          <TableCell align="right" sx={{ color: row.resolution_rate > 70 ? '#10b981' : '#f59e0b' }}>
                            {row.resolution_rate || 0}%
                          </TableCell>
                          <TableCell align="right">{row.avg_days_open || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {subTab === 2 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Geographic Hotspots - High Activity Areas</Typography>
                  <Table size="small" sx={{ fontSize: '0.9rem' }}>
                    <TableHead sx={{ bgcolor: '#f3f4f6' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>District</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Complaint Density</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Last 7 Days</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Last 30 Days</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Local Res. Rate %</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Category Diversity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {geographicHotspots.slice(0, 30).map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                          <TableCell sx={{ fontWeight: 500, color: row.complaint_density > 100 ? '#dc2626' : '#f59e0b' }}>
                            {row.district_name}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ bgcolor: row.complaint_density > 100 ? '#fee2e2' : '#fef3c7', p: '4px 8px', borderRadius: 1, display: 'inline-block' }}>
                              {row.complaint_density || 0}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{row.last_7_days || 0}</TableCell>
                          <TableCell align="right">{row.last_30_days || 0}</TableCell>
                          <TableCell align="right">{row.local_resolution_rate || 0}%</TableCell>
                          <TableCell align="right">{row.category_diversity || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}

          {/* SEVERITY TAB */}
          {mainTab === 8 && (
            <Box>
              <Tabs value={subTab} onChange={handleSubTabChange} sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}>
                <Tab label="Severity Classification" />
                <Tab label="Complaint Types" />
                <Tab label="Volume Trends" />
              </Tabs>

              {subTab === 0 && (
                <Box>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Issues by Severity Level</Typography>
                  <Grid container spacing={2}>
                    {severityClassification.slice(0, 4).map((row, idx) => {
                      const severityColor = {
                        'Critical': '#dc2626',
                        'High': '#f59e0b',
                        'Medium': '#3b82f6',
                        'Low': '#10b981'
                      };
                      return (
                        <Grid item xs={12} sm={6} md={3} key={idx}>
                          <Card sx={{ bgcolor: '#ffffff', border: `2px solid ${severityColor[row.severity_level] || '#e5e7eb'}` }}>
                            <CardContent>
                              <Typography sx={{ color: severityColor[row.severity_level], fontWeight: 700, mb: 1 }}>
                                {row.severity_level} Issues
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                {row.total_complaints || 0}
                              </Typography>
                              <Typography sx={{ fontSize: '0.85rem', color: '#6b7280', mb: 1 }}>
                                {row.most_common_category}
                              </Typography>
                              <LinearProgress variant="determinate" value={row.resolution_rate || 0} sx={{ mb: 1 }} />
                              <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                Resolved: {row.resolution_rate || 0}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {subTab === 1 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Complaint Type Breakdown</Typography>
                  <Table size="small" sx={{ fontSize: '0.9rem' }}>
                    <TableHead sx={{ bgcolor: '#f3f4f6' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Complaint Type</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Users</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Res. Rate %</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Rating</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Satisfied %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {complaintTypeAnalytics.slice(0, 20).map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{row.complaint_type}</TableCell>
                          <TableCell align="right">{row.total_count || 0}</TableCell>
                          <TableCell align="right">{row.unique_users || 0}</TableCell>
                          <TableCell align="right">{row.resolved_count || 0}</TableCell>
                          <TableCell align="right">{row.resolution_rate || 0}%</TableCell>
                          <TableCell align="right">{row.avg_satisfaction || 0}</TableCell>
                          <TableCell align="right">{row.satisfaction_percentage || 0}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {subTab === 2 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Daily Complaint Volume Trends (Last 90 Days)</Typography>
                  <Table size="small" sx={{ fontSize: '0.9rem' }}>
                    <TableHead sx={{ bgcolor: '#f3f4f6' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Day</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Complaints Today</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>In Progress</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Districts Active</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>New Users</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {complaintVolumeTrends.slice(0, 40).map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' }, fontSize: '0.85rem' }}>
                          <TableCell>{row.date}</TableCell>
                          <TableCell>{row.day_name}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ bgcolor: '#dbeafe', p: '2px 6px', borderRadius: 1, display: 'inline-block' }}>
                              {row.complaints_today || 0}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#10b981' }}>{row.resolved_today || 0}</TableCell>
                          <TableCell align="right" sx={{ color: '#f59e0b' }}>{row.in_progress_today || 0}</TableCell>
                          <TableCell align="right">{row.districts_active || 0}</TableCell>
                          <TableCell align="right">{row.users_today || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};


export default AdvancedAnalytics;
